import React, { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { TextField, Typography, IconButton, Tooltip, InputAdornment } from '@mui/material';
import Delete from '@mui/icons-material/Delete';
import AutocompleteInput from '../components/AutocompleteInput';

const useOrderTable = ({
    control,
    errors,
    isEditMode,
    hasLimit,
    axiosInstance,
    selectedClient,
    orderData,
    setOrderData,
    calculateSellingPriceIncludingTax,
    calculateItemSubtotal,
    calculateTotalAmount,
    formatCurrency,
    handleItemDelete,
}) => {

    const updateItem = (index, field, value) => {
        const updatedItems = [...orderData.items];
        const updatedItem = { ...updatedItems[index] };

        // 値反映
        updatedItem[field] = value;

        // 数値に正規化
        const sellingPrice = Number(updatedItem.selling_price ?? 0);
        const taxRate = Number(updatedItem.tax_rate ?? 0);
        const quantity = Number(updatedItem.quantity ?? 0);

        // 税込単価
        updatedItem.selling_price_including_tax = calculateSellingPriceIncludingTax(sellingPrice, taxRate);

        // サブトータル
        const { subtotal, subtotal_including_tax } =
            calculateItemSubtotal({
                price: updatedItem.selling_price,
                quantity,
                tax_rate: updatedItem.tax_rate,
            });

        updatedItem.subtotal = subtotal;
        updatedItem.subtotal_including_tax = subtotal_including_tax;

        updatedItems[index] = updatedItem;

        // 合計
        const { total_amount, total_amount_including_tax } = calculateTotalAmount(updatedItems);

        setOrderData({
            ...orderData,
            items: updatedItems,
            total_amount,
            total_amount_including_tax,
        });
    };

    const handleItemChange = (e, index, selectedProduct = null) => {
        const { name, value } = e.target;

        const updatedItems = [...orderData.items];

        let updatedItem = {
            ...updatedItems[index],
            [name]: value,
        };

        if (selectedProduct) {
            const price = selectedProduct.price ?? 0;
            const sellingPrice =
                selectedProduct.client_price != null
                    ? Number(selectedProduct.client_price)
                    : Number(selectedProduct.price ?? 0);

            const taxRate = selectedProduct.tax_rate ?? 0;

            updatedItem = {
                ...updatedItem,
                variant_id: selectedProduct.variant_id ?? '',
                product_id: selectedProduct.product_id ?? '',
                sku: selectedProduct.sku ?? '',
                product_name:
                    e.target.sku_product_name ?? selectedProduct.product_name ?? '',
                option_combination: selectedProduct.option_combination ?? '',
                price,
                selling_price: sellingPrice,
                tax_rate: taxRate,
                selling_price_including_tax: calculateSellingPriceIncludingTax(sellingPrice, taxRate),
            };
        }
        updatedItem = {
            ...updatedItem,
            ...calculateItemSubtotal({
                price: updatedItem.selling_price,
                quantity: updatedItem.quantity,
                tax_rate: updatedItem.tax_rate,
            }),
        };
        updatedItems[index] = updatedItem;

        const { total_amount, total_amount_including_tax } = calculateTotalAmount(updatedItems);

        setOrderData({
            ...orderData,
            items: updatedItems,
            total_amount,
            total_amount_including_tax,
        });
    };

    const columns = useMemo(() => [
        {
            label: '品番',
            accessor: 'sku',
            render: (item, index) => (
                <Controller
                    name={`items.${index}.sku`}
                    control={control}
                    rules={{
                        required: '品番は必須です',
                        maxLength: {
                            value: 100,
                            message: '100文字以内で入力してください',
                        },
                    }}
                    render={({ field }) => (
                        <AutocompleteInput
                            hasLimit={hasLimit}
                            value={field.value || ''}
                            index={index}
                            //isEditMode={isEditMode}
                            isEditMode={isEditMode && !item.variant_id}
                            fetchSuggestions={async (keyword) => {
                                try {
                                    const res = await axiosInstance.post('/product/sku_suggest', {
                                        keyword,
                                        client_id:
                                            selectedClient?.client_id ??
                                            orderData?.client_id ??
                                            null,
                                    });
                                    return res.data;
                                } catch (err) {
                                    console.error(err);
                                    return [];
                                }
                            }}
                            onChange={(e, idx, selectedProduct) => {
                                const value =
                                    selectedProduct?.sku ||
                                    e?.target?.value ||
                                    '';

                                field.onChange(value);
                                handleItemChange(e, idx, selectedProduct);
                            }}
                            error={!!errors.items?.[index]?.sku}
                            helperText={errors.items?.[index]?.sku?.message}
                        />
                    )}
                />
            ),
            width: '200px',
        },
        {
            label: '商品名',
            accessor: 'product_name',
            render: (item, index) => (
                <>
                    <TextField
                        value={item.product_name ?? ''}
                        disabled={!isEditMode}
                        size="small"
                        multiline
                        inputProps={{
                            maxLength: 255
                        }}
                        onChange={(e) => {
                            updateItem(index, 'product_name', e.target.value);
                        }}
                    />

                    <Typography variant="caption" color="text.secondary">
                        定価 {formatCurrency(item.price ?? 0)}
                    </Typography>
                </>
            ),
        },
        {
            label: '数量',
            accessor: 'quantity',
            render: (item, index) => (
                <Controller
                    name={`items.${index}.quantity`}
                    control={control}
                    rules={{
                        validate: value => {
                            if (value === '' || value == null) return '数量は必須です';
                            if (!/^\d+$/.test(String(value))) return '半角数字で入力してください';
                            if (parseInt(value, 10) < 1) return '1以上の数で入力してください';
                            if (parseInt(value, 10) > 999999) return '6桁以内で入力してください';
                            return true;
                        },
                    }}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            type="text"
                            disabled={!isEditMode}
                            error={!!errors.items?.[index]?.quantity}
                            helperText={errors.items?.[index]?.quantity?.message}
                            sx={{ width: 120 }}
                            inputProps={{
                                maxLength: 6,
                                inputMode: 'numeric',
                            }}
                            onChange={(e) => {
                                const raw = e.target.value;
                                field.onChange(raw);
                                // 整数チェックして変換
                                if (!/^\d+$/.test(raw)) return;
                                updateItem(index, 'quantity', parseInt(raw, 10));
                            }}
                        />
                    )}
                />
            ),
            minWidth: '90px',
        },
        {
            label: '販売単価',
            accessor: 'selling_price',
            render: (item, index) => (
                <TextField
                    value={item.selling_price ?? ''}
                    type="text"
                    disabled={!isEditMode}
                    size="small"
                    sx={{ width: 120 }}
                    inputProps={{
                        inputMode: 'numeric',
                        maxLength: 8
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">円</InputAdornment>
                        ),
                    }}
                    onChange={(e) => {
                        const raw = e.target.value;

                        if (!/^\d*$/.test(raw)) return;
                        if (raw !== '' && Number(raw) > 99999999) return;

                        updateItem(
                            index,
                            'selling_price',
                            raw === '' ? 0 : Number(raw)
                        );
                    }}
                />
            ),
            width: '100px',
        },
        {
            label: '消費税',
            accessor: 'tax_rate',
            render: (item, index) => (
                <TextField
                    value={item.tax_rate ?? ''}
                    type="text"
                    disabled={!isEditMode}
                    error={!!errors.items?.[index]?.tax_rate}
                    helperText={errors.items?.[index]?.tax_rate?.message}
                    sx={{ width: 80 }}
                    inputProps={{
                        maxLength: 3,
                        inputMode: 'numeric',
                    }}
                    onChange={(e) => {
                        const raw = e.target.value;

                        if (
                            /^\d*$/.test(raw) &&
                            (raw === '' || parseInt(raw, 10) <= 100)
                        ) {
                            updateItem(
                                index,
                                'tax_rate',
                                raw === '' ? 0 : parseInt(raw, 10)
                            );
                        }
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">%</InputAdornment>
                        ),
                    }}
                />
            ),
        },
        {
            label: '金額',
            accessor: 'subtotal',
            render: (item) => (
                <>
                    {formatCurrency(item.subtotal ?? 0)}<br />
                    <small>税込 {formatCurrency(item.subtotal_including_tax ?? 0)}</small>
                </>
            ),
            width: '120px',
        },
        ...(
            (
                !orderData?.order_status_id ||
                ['1', '2', '3'].includes(String(orderData?.order_status_id)) ||
                !orderData?.estimate_status_id ||
                ['1', '2'].includes(String(orderData?.estimate_status_id)) ||
                !orderData?.invoice_status_id ||
                ['1'].includes(String(orderData?.invoice_status_id))
            ) ? [{
                label: '',
                accessor: 'delete',
                render: (item, index) => (
                    <Tooltip title="削除">
                        <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleItemDelete(index, item.invoice_item_id || item.order_item_id || item.estimate_item_id)}
                        >
                            <Delete />
                        </IconButton>
                    </Tooltip>
                ),
            },] : []
        ),
    ], [
        orderData,
        isEditMode,
        errors,
        control,
        hasLimit,
    ]);

    return {
        columns,
        updateItem,
        handleItemChange,
    };
};

export default useOrderTable;